import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, Shield, Eye, Settings, 
  AlertTriangle, CheckCircle, Zap, BarChart3
} from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';

interface NISTFunction {
  id: string;
  name: string;
  description: string;
  icon: any;
  color: string;
  asis: number;
  target: number;
  subcategories: {
    id: string;
    name: string;
    description: string;
    asis: number;
    target: number;
    outcomes: string[];
  }[];
}

interface Slide {
  id: string;
  title: string;
  subtitle?: string;
  content: React.ReactNode;
  icon: any;
  color: string;
}

interface NISTPresentationProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NISTPresentation({ isOpen, onClose }: NISTPresentationProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedFunction, setSelectedFunction] = useState<NISTFunction | null>(null);

  const nistFunctions: NISTFunction[] = [
    {
      id: 'identify',
      name: 'IDENTIFY',
      description: 'Développer une compréhension organisationnelle pour gérer les risques de cybersécurité',
      icon: Eye,
      color: '#3498DB',
      asis: 65,
      target: 85,
      subcategories: [
        {
          id: 'asset-management',
          name: 'Asset Management',
          description: 'Inventaire et classification des actifs',
          asis: 70,
          target: 90,
          outcomes: ['Inventaire complet', 'Classification des données', 'Propriétaires identifiés']
        },
        {
          id: 'business-environment',
          name: 'Business Environment',
          description: 'Compréhension du contexte métier',
          asis: 60,
          target: 85,
          outcomes: ['Mission claire', 'Objectifs définis', 'Rôles établis']
        },
        {
          id: 'governance',
          name: 'Governance',
          description: 'Politiques et procédures de sécurité',
          asis: 55,
          target: 80,
          outcomes: ['Politiques documentées', 'Procédures établies', 'Conformité assurée']
        },
        {
          id: 'risk-assessment',
          name: 'Risk Assessment',
          description: 'Évaluation et gestion des risques',
          asis: 75,
          target: 90,
          outcomes: ['Risques identifiés', 'Priorités établies', 'Plans de mitigation']
        },
        {
          id: 'risk-management',
          name: 'Risk Management Strategy',
          description: 'Stratégie globale de gestion des risques',
          asis: 65,
          target: 85,
          outcomes: ['Stratégie définie', 'Tolérance au risque', 'Décisions éclairées']
        },
        {
          id: 'supply-chain',
          name: 'Supply Chain Risk Management',
          description: 'Gestion des risques de la chaîne d\'approvisionnement',
          asis: 50,
          target: 75,
          outcomes: ['Fournisseurs évalués', 'Contrats sécurisés', 'Monitoring continu']
        }
      ]
    },
    {
      id: 'protect',
      name: 'PROTECT',
      description: 'Développer et implémenter des protections pour assurer la délivrance des services critiques',
      icon: Shield,
      color: '#2ECC71',
      asis: 78,
      target: 90,
      subcategories: [
        {
          id: 'identity-management',
          name: 'Identity Management',
          description: 'Gestion des identités et accès',
          asis: 80,
          target: 95,
          outcomes: ['IAM implémenté', 'MFA activé', 'Privilèges minimaux']
        },
        {
          id: 'protective-technology',
          name: 'Protective Technology',
          description: 'Technologies de protection',
          asis: 75,
          target: 90,
          outcomes: ['Firewalls configurés', 'Antivirus à jour', 'Monitoring actif']
        },
        {
          id: 'maintenance',
          name: 'Maintenance',
          description: 'Maintenance des systèmes et équipements',
          asis: 70,
          target: 85,
          outcomes: ['Patches appliqués', 'Maintenance préventive', 'Inventaire à jour']
        },
        {
          id: 'protective-technology',
          name: 'Protective Technology',
          description: 'Technologies de protection',
          asis: 75,
          target: 90,
          outcomes: ['Firewalls configurés', 'Antivirus à jour', 'Monitoring actif']
        },
        {
          id: 'awareness-training',
          name: 'Awareness & Training',
          description: 'Formation et sensibilisation',
          asis: 65,
          target: 80,
          outcomes: ['Formation régulière', 'Tests de phishing', 'Culture sécurité']
        },
        {
          id: 'data-security',
          name: 'Data Security',
          description: 'Protection des données',
          asis: 85,
          target: 95,
          outcomes: ['Chiffrement actif', 'Sauvegardes sécurisées', 'Classification données']
        }
      ]
    },
    {
      id: 'detect',
      name: 'DETECT',
      description: 'Développer et implémenter des activités pour identifier l\'occurrence d\'un événement de cybersécurité',
      icon: AlertTriangle,
      color: '#F39C12',
      asis: 72,
      target: 88,
      subcategories: [
        {
          id: 'anomalies-events',
          name: 'Anomalies & Events',
          description: 'Détection d\'anomalies et événements',
          asis: 70,
          target: 90,
          outcomes: ['Monitoring 24/7', 'Alertes automatiques', 'Corrélation événements']
        },
        {
          id: 'security-monitoring',
          name: 'Security Continuous Monitoring',
          description: 'Surveillance continue de la sécurité',
          asis: 75,
          target: 85,
          outcomes: ['SIEM implémenté', 'Logs centralisés', 'Analyse comportementale']
        },
        {
          id: 'detection-processes',
          name: 'Detection Processes',
          description: 'Processus de détection',
          asis: 68,
          target: 88,
          outcomes: ['Procédures définies', 'Escalade automatique', 'Réponse rapide']
        }
      ]
    },
    {
      id: 'respond',
      name: 'RESPOND',
      description: 'Développer et implémenter des activités pour agir face à un événement de cybersécurité détecté',
      icon: Zap,
      color: '#E74C3C',
      asis: 68,
      target: 85,
      subcategories: [
        {
          id: 'response-planning',
          name: 'Response Planning',
          description: 'Planification de la réponse',
          asis: 65,
          target: 85,
          outcomes: ['Plans documentés', 'Rôles définis', 'Procédures testées']
        },
        {
          id: 'communications',
          name: 'Communications',
          description: 'Communication pendant l\'incident',
          asis: 70,
          target: 90,
          outcomes: ['Canaux établis', 'Templates prêts', 'Stakeholders informés']
        },
        {
          id: 'analysis',
          name: 'Analysis',
          description: 'Analyse de l\'incident',
          asis: 60,
          target: 80,
          outcomes: ['Analyse forensique', 'Cause racine', 'Impact évalué']
        },
        {
          id: 'mitigation',
          name: 'Mitigation',
          description: 'Atténuation des effets',
          asis: 75,
          target: 90,
          outcomes: ['Containment rapide', 'Récupération efficace', 'Leçons apprises']
        },
        {
          id: 'improvements',
          name: 'Improvements',
          description: 'Améliorations post-incident',
          asis: 55,
          target: 80,
          outcomes: ['Post-mortem', 'Améliorations', 'Prévention future']
        }
      ]
    },
    {
      id: 'recover',
      name: 'RECOVER',
      description: 'Développer et implémenter des activités pour maintenir la résilience et restaurer les capacités',
      icon: CheckCircle,
      color: '#9B59B6',
      asis: 70,
      target: 87,
      subcategories: [
        {
          id: 'recovery-planning',
          name: 'Recovery Planning',
          description: 'Planification de la récupération',
          asis: 65,
          target: 85,
          outcomes: ['Plans de récupération', 'RTO/RPO définis', 'Tests réguliers']
        },
        {
          id: 'improvements',
          name: 'Improvements',
          description: 'Améliorations continues',
          asis: 70,
          target: 90,
          outcomes: ['Leçons apprises', 'Processus améliorés', 'Formation mise à jour']
        },
        {
          id: 'communications',
          name: 'Communications',
          description: 'Communication de récupération',
          asis: 75,
          target: 85,
          outcomes: ['Communication claire', 'Stakeholders informés', 'Confiance restaurée']
        }
      ]
    },
    {
      id: 'govern',
      name: 'GOVERN',
      description: 'Établir et surveiller la stratégie de cybersécurité, la posture et les programmes',
      icon: Settings,
      color: '#34495E',
      asis: 75,
      target: 90,
      subcategories: [
        {
          id: 'organizational-context',
          name: 'Organizational Context',
          description: 'Contexte organisationnel',
          asis: 80,
          target: 95,
          outcomes: ['Mission claire', 'Objectifs alignés', 'Culture sécurité']
        },
        {
          id: 'risk-management',
          name: 'Risk Management Strategy',
          description: 'Stratégie de gestion des risques',
          asis: 70,
          target: 90,
          outcomes: ['Stratégie définie', 'Tolérance au risque', 'Décisions éclairées']
        },
        {
          id: 'cybersecurity-supply-chain',
          name: 'Cybersecurity Supply Chain',
          description: 'Chaîne d\'approvisionnement cybersécurité',
          asis: 65,
          target: 85,
          outcomes: ['Fournisseurs évalués', 'Contrats sécurisés', 'Monitoring continu']
        },
        {
          id: 'roles-responsibilities',
          name: 'Roles & Responsibilities',
          description: 'Rôles et responsabilités',
          asis: 75,
          target: 90,
          outcomes: ['RACI défini', 'Responsabilités claires', 'Accountability établie']
        },
        {
          id: 'oversight',
          name: 'Oversight',
          description: 'Surveillance et gouvernance',
          asis: 70,
          target: 85,
          outcomes: ['Comité sécurité', 'Rapports réguliers', 'Décisions documentées']
        },
        {
          id: 'cybersecurity-strategy',
          name: 'Cybersecurity Strategy',
          description: 'Stratégie cybersécurité',
          asis: 80,
          target: 95,
          outcomes: ['Vision claire', 'Roadmap définie', 'Ressources allouées']
        }
      ]
    }
  ];

  const slides: Slide[] = [
    {
      id: 'title',
      title: 'Cadre NIST Cybersecurity 2.0',
      subtitle: 'Évaluation et Roadmap de Sécurité',
      icon: Shield,
      color: '#E74C3C',
      content: (
        <div className="text-center space-y-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-32 h-32 mx-auto bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center"
          >
            <Shield className="w-16 h-16 text-white" />
          </motion.div>
          
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-bz-dark">Cadre NIST Cybersecurity</h1>
            <h2 className="text-2xl text-red-600 font-semibold">Framework 2.0</h2>
            <p className="text-xl text-gray-600">Évaluation complète et roadmap de sécurité</p>
          </div>

          <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto">
            <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-xl">
              <div className="text-2xl font-bold text-red-600">6</div>
              <div className="text-sm text-red-800">Fonctions</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
              <div className="text-2xl font-bold text-green-600">23</div>
              <div className="text-sm text-green-800">Catégories</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
              <div className="text-2xl font-bold text-blue-600">108</div>
              <div className="text-sm text-blue-800">Sous-catégories</div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'spider-overview',
      title: 'Vue d\'Ensemble - Spider Chart',
      subtitle: 'Maturité des 6 Fonctions NIST',
      icon: BarChart3,
      color: '#3498DB',
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-l-4 border-blue-500">
            <h3 className="text-xl font-bold text-blue-800 mb-4">🕸️ Maturité Globale NIST</h3>
            <p className="text-gray-700">Évaluation ASIS vs Target pour les 6 fonctions principales</p>
          </div>

          {/* Spider Chart Fonctionnel */}
          <div className="bg-white rounded-lg p-8 shadow-md">
            <h4 className="text-lg font-semibold text-gray-800 mb-6 text-center">
              🕸️ Maturité Globale NIST - Spider Chart
            </h4>
            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={nistFunctions.map(func => ({
                  function: func.name,
                  asis: func.asis,
                  target: func.target,
                  minRequired: 70
                }))}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="function" />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} />
                  <Radar
                    name="ASIS"
                    dataKey="asis"
                    stroke="#3498DB"
                    fill="#3498DB"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                  <Radar
                    name="Target"
                    dataKey="target"
                    stroke="#2ECC71"
                    fill="#2ECC71"
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                  <Radar
                    name="Minimum"
                    dataKey="minRequired"
                    stroke="#E74C3C"
                    fill="none"
                    strokeWidth={1}
                    strokeDasharray="5 5"
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span>ASIS</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span>Target</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 border border-red-500 rounded"></div>
                <span>Minimum (70%)</span>
              </div>
            </div>
          </div>

          {/* Functions Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {nistFunctions.map((func, index) => {
              const Icon = func.icon;
              return (
                <motion.div
                  key={func.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setSelectedFunction(func)}
                  className="bg-white p-4 rounded-lg shadow-sm border cursor-pointer hover:shadow-lg hover:border-bz-blue transition-all group"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${func.color}20` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: func.color }} />
                    </div>
                    <h4 className="font-bold text-gray-800 group-hover:text-bz-blue transition-colors">
                      {func.name}
                    </h4>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">{func.description}</p>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">ASIS: {func.asis}%</span>
                      <span className="text-gray-500">Target: {func.target}%</span>
                    </div>
                    <div className="bg-gray-200 rounded-full h-2">
                      <div className="relative h-full">
                        <div
                          className="absolute h-full bg-gray-400"
                          style={{ width: `${func.asis}%` }}
                        />
                        <div
                          className="absolute h-full rounded-full"
                          style={{ 
                            width: `${func.target}%`,
                            backgroundColor: func.color
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />
          
          {/* Main Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div 
                className="p-6 text-white"
                style={{ backgroundColor: currentSlideData.color }}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <currentSlideData.icon className="w-8 h-8" />
                    <div>
                      <h2 className="text-2xl font-bold">{currentSlideData.title}</h2>
                      {currentSlideData.subtitle && (
                        <p className="text-white/90">{currentSlideData.subtitle}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Slide {currentSlide + 1} sur {slides.length}</span>
                    <span>{Math.round(((currentSlide + 1) / slides.length) * 100)}%</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                      transition={{ duration: 0.3 }}
                      className="bg-white h-2 rounded-full"
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 max-h-[calc(90vh-200px)] overflow-y-auto">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                  >
                    {currentSlideData.content}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center p-6 border-t bg-gray-50">
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Précédent
                </button>
                
                <div className="flex gap-2">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        index === currentSlide ? 'bg-bz-blue' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={nextSlide}
                  disabled={currentSlide === slides.length - 1}
                  className="flex items-center gap-2 px-4 py-2 bg-bz-blue hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Suivant
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Function Detail Modal */}
          <AnimatePresence>
            {selectedFunction && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[60] flex items-center justify-center p-4"
                onClick={() => setSelectedFunction(null)}
              >
                <div className="absolute inset-0 bg-black/60" />
                <motion.div
                  initial={{ scale: 0.9, y: 20 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.9, y: 20 }}
                  onClick={(e) => e.stopPropagation()}
                  className="relative bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[80vh] overflow-y-auto"
                >
                  {/* Function Header */}
                  <div
                    className="p-6 text-white rounded-t-xl"
                    style={{ backgroundColor: selectedFunction.color }}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-4">
                        <selectedFunction.icon className="w-8 h-8" />
                        <div>
                          <h3 className="text-2xl font-bold">{selectedFunction.name}</h3>
                          <p className="text-white/90">{selectedFunction.description}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedFunction(null)}
                        className="text-white hover:bg-white/10 rounded-full p-2"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Function Content */}
                  <div className="p-6">
                    {/* Spider Chart for Function */}
                    <div className="mb-8">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">
                        🕸️ Spider Chart - {selectedFunction.name}
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-6">
                        <div className="h-80 w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={selectedFunction.subcategories.map(sub => ({
                              subcategory: sub.name.length > 15 ? sub.name.substring(0, 15) + '...' : sub.name,
                              asis: sub.asis,
                              target: sub.target,
                              minRequired: 60
                            }))}>
                              <PolarGrid />
                              <PolarAngleAxis dataKey="subcategory" />
                              <PolarRadiusAxis angle={90} domain={[0, 100]} />
                              <Radar
                                name="ASIS"
                                dataKey="asis"
                                stroke={selectedFunction.color}
                                fill={selectedFunction.color}
                                fillOpacity={0.3}
                                strokeWidth={2}
                              />
                              <Radar
                                name="Target"
                                dataKey="target"
                                stroke="#2ECC71"
                                fill="#2ECC71"
                                fillOpacity={0.2}
                                strokeWidth={2}
                              />
                              <Radar
                                name="Minimum"
                                dataKey="minRequired"
                                stroke="#E74C3C"
                                fill="none"
                                strokeWidth={1}
                                strokeDasharray="5 5"
                              />
                            </RadarChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="flex justify-center gap-6 mt-4 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: selectedFunction.color }}></div>
                            <span>ASIS</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-green-500 rounded"></div>
                            <span>Target</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 border border-red-500 rounded"></div>
                            <span>Minimum (60%)</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Subcategories */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">
                        Sous-catégories ({selectedFunction.subcategories.length})
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedFunction.subcategories.map((sub, index) => (
                          <motion.div
                            key={sub.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                          >
                            <div className="flex justify-between items-start mb-3">
                              <h5 className="font-bold text-gray-800">{sub.name}</h5>
                              <div className="text-right">
                                <div className="text-xs text-gray-500">ASIS: {sub.asis}%</div>
                                <div className="text-xs text-gray-500">Target: {sub.target}%</div>
                              </div>
                            </div>
                            
                            <p className="text-sm text-gray-600 mb-3">{sub.description}</p>
                            
                            <div className="space-y-2">
                              <div className="bg-gray-200 rounded-full h-2">
                                <div className="relative h-full">
                                  <div
                                    className="absolute h-full bg-gray-400"
                                    style={{ width: `${sub.asis}%` }}
                                  />
                                  <div
                                    className="absolute h-full rounded-full"
                                    style={{
                                      width: `${sub.target}%`,
                                      backgroundColor: selectedFunction.color
                                    }}
                                  />
                                </div>
                              </div>
                              
                              <div>
                                <h6 className="text-xs font-medium text-gray-700 mb-1">Outcomes attendus:</h6>
                                <ul className="text-xs text-gray-600 space-y-1">
                                  {sub.outcomes.map((outcome, idx) => (
                                    <li key={idx} className="flex items-center gap-1">
                                      <CheckCircle className="w-3 h-3 text-green-500" />
                                      {outcome}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}
